import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyServices } from '../services/api';
import { SERVICE_STATUS } from '../lib/constants';

const MyServices = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (isAuthenticated) {
        try {
          setLoading(true);
          const data = await getMyServices();
          setServices(data);
        } catch (error) {
          console.error('Failed to fetch services:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to view your services.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status, statusReason) => {
    switch (status) {
      case SERVICE_STATUS.APPROVED:
        return (
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium" data-testid="status-badge-approved">
            ✅ Approved
          </span>
        );
      case SERVICE_STATUS.PENDING:
        return (
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium" data-testid="status-badge-pending">
            ⏳ Pending Review
          </span>
        );
      case SERVICE_STATUS.REJECTED:
        return (
          <div className="flex flex-col items-end">
            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium mb-1" data-testid="status-badge-rejected">
              ❌ Rejected
            </span>
            {statusReason && (
              <span className="text-xs text-red-600" data-testid="rejection-reason">
                Reason: {statusReason}
              </span>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="my-services-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="my-services-title">
              My Services
            </h1>
            <p className="text-gray-600 mt-2">Manage your registered mental health services</p>
          </div>
          <button
            onClick={() => navigate('/register-service')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
            data-testid="register-new-service-button"
          >
            + Register New Service
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading your services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="no-services">
            <div className="text-6xl mb-4">📜</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Services Yet</h3>
            <p className="text-gray-600 mb-6">You haven't registered any services yet. Get started by adding your first service.</p>
            <button
              onClick={() => navigate('/register-service')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              Register Your First Service
            </button>
          </div>
        ) : (
          <div className="grid gap-6" data-testid="services-list">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-md p-6" data-testid="service-item">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-testid="service-item-name">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 line-clamp-2" data-testid="service-item-description">
                      {service.description}
                    </p>
                  </div>
                  <div className="ml-4">{getStatusBadge(service.status, service.status_reason)}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <p className="font-medium" data-testid="service-item-location">
                      {service.city}, {service.state}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <p className="font-medium" data-testid="service-item-phone">
                      {service.contact_phone}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Cost:</span>
                    <p className="font-medium capitalize" data-testid="service-item-cost">
                      {service.cost}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Mode:</span>
                    <p className="font-medium" data-testid="service-item-mode">
                      {service.mode.join(', ')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {service.service_types.slice(0, 3).map((type, index) => (
                    <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                      {type}
                    </span>
                  ))}
                  {service.service_types.length > 3 && (
                    <span className="text-xs text-gray-500">+{service.service_types.length - 3} more</span>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  {service.status === SERVICE_STATUS.APPROVED && (
                    <button
                      onClick={() => navigate(`/service/${service.id}`)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      data-testid="view-public-button"
                    >
                      View Public Page
                    </button>
                  )}
                  {service.status === SERVICE_STATUS.REJECTED && (
                    <button
                      onClick={() => navigate('/register-service')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      data-testid="resubmit-button"
                    >
                      Submit New Service
                    </button>
                  )}
                  <span className="text-gray-400 text-sm">
                    Submitted: {new Date(service.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServices;
